import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ArrowLeft, ArrowLeftRight, Building2, ChevronLeft, ChevronRight, Eye, FolderClosed,
  KeyRound, LayoutDashboard, Megaphone, Menu, MessageSquare, Network, PieChart,
  Settings as SettingsIcon, Sparkles, Users, X, type LucideIcon,
} from 'lucide-react';
import { WorkspaceSwitcher } from '../workspace/WorkspaceSwitcher';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { ADVISOR_HOLD_MAX_MS, rememberedFirmWorkspaceId, useAdvisorFirm, useBoundedHold, useHoldForAdvisorQuery } from '../advisor/useAdvisorFirm';
import { isFirmAdminWorkspace } from '../advisor/firmAdmin';
import { useWorkspaceKind } from '../workspace/useWorkspaceKind';
import { useClientFrame, ADVISOR_CLIENTS_PATH } from '../advisor/useClientFrame';
import { firmAssistantEnabled } from '../lib/appEnv';
import { useCredentialsReadProbe } from '../lib/useCanReadCredentials';
import { useCanReadCashFlow } from '../lib/useCanReadCashFlow';
import { useCanUseAssistant } from '../lib/useCanUseAssistant';
import { useBrand } from '../brand/BrandProvider';
import { STEWARD, WithSteward } from './WithSteward';
import { useBrandLogo } from '../brand/useBrandLogo';
import { AccountMenu } from './AccountMenu';
import { BreadcrumbProvider, Breadcrumbs } from './Breadcrumbs';
import { DocumentTitle } from './DocumentTitle';
import { UserText } from './UserText';
import { UnsavedChangesGuard } from '../guards/UnsavedChangesGuard';
import { TourProvider, useTourOptional } from '../tour/TourProvider';
import { EnvPill, EnvTopStrip } from './EnvIndicator';
import { OfflineBanner } from './OfflineBanner';
import { ViewOnlyPill } from './ViewOnlyPill';
import { useFeedbackUnread } from '../feedback/useFeedback';
import { feedbackNavLabel } from '../feedback/unread';
import { useAnnouncementsUnread } from '../announcements/useAnnouncements';
import { announcementsNavLabel } from '../announcements/unread';
import { AnnouncementPopup } from '../announcements/AnnouncementPopup';
import { useOnboardingRequestWaiting } from '../onboardingrequest/useOnboardingRequests';
import { useOnboardingTaxIdCopy } from '../onboarding/client/useOnboardingTaxIdCopy';
import { useOnboardingSelfLink } from '../onboarding/client/useOnboardingSelfLink';
import { peopleNavLabel } from '../onboardingrequest/requests';
import { AssistantProvider } from '../assistant/AssistantProvider';
import { AssistantPanel, ASSISTANT_PANEL_ID, ASSISTANT_TRIGGER_ID } from '../assistant/AssistantPanel';
import { useAssistant } from '../assistant/useAssistant';

// Lucide with a uniform stroke across the app (docs/04-navigation-ia.md §1).
export const ICON_STROKE = 1.75;

/**
 * Flat pillars only — sub-pages live in in-page tab bars (docs/04-navigation-ia.md §2).
 *
 * Exported since steward-app#1425 so `standingNavigation.test.ts` can check §3.3's rule
 * ("signposts are never homes": every `firstRunSteps()` href must point at a page reachable
 * through standing navigation) against the REAL list rather than a copy of it. It is read
 * there, never rendered — this file remains the only thing that renders it.
 */
export const NAV: { to: string; label: string; icon: LucideIcon; end: boolean }[] = [
  { to: '/',            label: 'Overview',   icon: LayoutDashboard, end: true  },
  { to: '/portfolio',   label: 'Portfolio',  icon: PieChart,        end: false },
  { to: '/entities',    label: 'Entities',   icon: Network,         end: false },
  { to: '/documents',   label: 'Documents',  icon: FolderClosed,    end: false },
  // People (steward-app#1425): the Family roster, with Contacts folded in as its second
  // tab. Contacts is no longer a pillar of its own — two people-shaped pillars in a flat
  // rail is the confusion that forced the Contacts→Members signpost in the first place, and
  // folding retires that signpost rather than adding a second. The list stays at six.
  { to: '/people',      label: 'People',     icon: Users,           end: false },
  { to: '/credentials', label: 'Credentials', icon: KeyRound,       end: false },
];

/**
 * The Cash flow pillar (steward-app#2592), inserted between Portfolio and Entities when
 * `useCanReadCashFlow` says yes. Kept OUT of {@link NAV} on purpose: that list is the standing
 * six, pinned by `standingNavigation.test.ts` and docs/04, and this item is capability-gated
 * like Credentials. Its `to` is the board (`/cash-flow`, steward-app#2593); Setup is reached from there.
 */
export const CASH_FLOW_NAV_ITEM: (typeof NAV)[number] =
  { to: '/cash-flow', label: 'Cash flow', icon: ArrowLeftRight, end: false };

/**
 * The advisor's own nav — steward-app#1401 narrowed this to render ONLY while standing in
 * the firm workspace (`SidebarContent`'s `inFirmWorkspace` branch); a client workspace gets
 * a single exit item instead (`useClientFrame`'s header), never these. So unlike before
 * #1401, this list IS the advisor's whole nav whenever it renders at all, never one of two
 * sections sharing the rail with a client's family pillars.
 *
 * `end: true` on Overview: it is the `/advisor` index route since 2026-09-11 (steward-app#1405),
 * and without `end` a plain prefix match would light this item up while sitting on
 * `/advisor/clients` too, the same reasoning NAV's own family Overview entry (`/`) already follows.
 * Clients moved off the index to `ADVISOR_CLIENTS_PATH` (`/advisor/clients`) the same day;
 * every #1401 exit (the band, this nav's exit item, the breadcrumb) uses that one constant
 * rather than a repeated literal, so the four cannot drift apart if the path moves again.
 *
 * ⚠️ **This dropped to THREE items on 2026-09-14 (steward-app#1429).** "Intake submissions"
 * (`/advisor/intakes`) is no longer a top-level item — it folded into Clients as a
 * "Prospects" tab (`AdvisorClientsLayout`, rendered inside `ADVISOR_CLIENTS_PATH`'s own
 * subtree), because a prospect's six stages and a client's onboarding rungs (#1426) are one
 * family's journey across one seam, and two separate rail items cut it in half. Do not
 * re-add an `/advisor/intakes` (or `/advisor/clients/prospects`) entry here — the tab bar is
 * the standing-navigation path to it now, per docs/04-navigation-ia.md §3.3.
 *
 * ⚠️ **Adding an item here does NOT make `Layout.tsx` fetch anything.** The Overview's
 * `GET /advisor/summary` is a per-client fan-out, and steward-app#661 is the scar from
 * putting one behind the nav: the page fetches it, this file renders a link to the page.
 * Do not import `useAdvisorSummary` into this file.
 *
 * `firmAdmin` adds "Firm" (steward-app#1361; renamed from "Your firm" steward-app#1732), last,
 * for a caller `isFirmAdminWorkspace` (`advisor/firmAdmin.ts`, steward-app#1742; was
 * `isFirmAdminHere` until then) says administers a firm workspace. The SAME predicate gates
 * the route (`RequireFirmAdmin`), so nobody is offered a link that refuses them outright — the
 * route itself may still show a loading, retry, or "choose the right firm" state rather than
 * the real page, for exactly the reasons `isFirmAdminWorkspace`'s own header explains. UX
 * gating in exactly the sense the rest of this list is: appcore decides what the page can read
 * and write.
 * It reads only values this layout already holds, so it adds no request either.
 *
 * ⚠️ **`showAdvisorItems` and `firmAdmin` are independent (steward-app#1742 review round 2,
 * R2).** Overview and Clients need a CONFIRMED advisor relationship (`advisorNavVisible`,
 * `advisorFor` or `firmChoice` truthy) before they mean anything, so `showAdvisorItems` still
 * carries that. Firm, since R1, does not: `isFirmAdminWorkspace` reduces to "is a workspace
 * admin, and the active workspace's packs already say `'firm'`" for the ordinary case, with no
 * dependency on the (separate) firm lookup those two items need — so `firmAdmin` can be `true`
 * (and `SidebarContent` below now renders this call) while `showAdvisorItems` is still `false`
 * during that lookup's loading or error window. Before this correction, the caller wrapped the
 * WHOLE list in one `advisorNavVisible`-gated condition, which hid "Firm" during exactly the
 * window `RequireFirmAdmin`'s own page stayed reachable in (R1), stranding an admin with no nav
 * link to a page they could otherwise use.
 */
function advisorNavItems(showAdvisorItems: boolean, firmAdmin: boolean): { to: string; label: string; icon: LucideIcon; end: boolean }[] {
  return [
    // Overview is the firm-level view and the `/advisor` index (steward-app#738, docs/26
    // §2a, steward-app#1405); Clients is the drill-down, moved to its own path the same day.
    ...(showAdvisorItems
      ? [
          { to: '/advisor',           label: 'Overview', icon: LayoutDashboard, end: true },
          { to: ADVISOR_CLIENTS_PATH, label: 'Clients',   icon: Users,          end: false },
        ]
      : []),
    ...(firmAdmin
      ? [{ to: '/advisor/firm', label: 'Firm', icon: Building2, end: false }]
      : []),
  ];
}

const COLLAPSE_STORAGE_KEY = 'fo-sidebar-collapsed';

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function navLinkClass(isActive: boolean, collapsed: boolean) {
  return `group relative flex items-center gap-2.5 px-3 py-2.5 mb-0.5 rounded-token no-underline text-[13px] transition-colors duration-200 border-r-2 ${
    collapsed ? 'justify-center' : ''
  } ${
    isActive ? 'font-semibold text-navy bg-cream border-bronze' : 'font-normal text-slate bg-transparent border-transparent'
  }`;
}

// Unread-reply indicators on the Feedback nav item (the app's first badged nav
// item; People's onboarding-request dot, steward-app#1427, reuses the dot shape in both
// modes). Two shapes for the two nav modes: a number when there is room for one,
// a dot on the collapsed rail when there isn't. Both are `aria-hidden` — the
// count reaches assistive tech through the link's accessible name
// (`feedbackNavLabel`), never through a floating glyph.
//
// `bg-navy text-surface` rather than bronze: the two tokens swap roles together
// in the dark theme, so the pair stays legible in both, whereas white-on-bronze
// is ~2.9:1. Written out in full because Tailwind v4 never sees an interpolated
// class name.
const navBadgeClass =
  'ml-auto inline-flex min-w-[1.125rem] shrink-0 items-center justify-center rounded-full bg-navy px-1.5 py-px text-[10px] font-bold leading-[1.4] tabular-nums text-surface';
const navBadgeDotClass =
  'absolute right-3 top-2 h-2 w-2 rounded-full bg-navy ring-2 ring-surface';

// Rail tooltip — shown on hover AND keyboard focus (docs/04 §4.3); native title is neither.
const railTooltipClass =
  'pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-token border border-line bg-surface px-2 py-1 font-sans text-[11px] font-semibold normal-case tracking-normal text-navy shadow-[0_4px_16px_-4px_rgba(15,23,42,0.2)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100';

interface SidebarContentProps {
  collapsed: boolean;
  /** Called on any navigation — the mobile drawer uses this to close itself. */
  onNavigate?: () => void;
  /** Present only on the desktop sidebar; the drawer has no rail mode. */
  onToggleCollapse?: () => void;
  /** Whether this instance carries the tour's `data-tour` anchors. Only the
   *  desktop sidebar does — the drawer copy would duplicate the selectors. */
  tourAnchors?: boolean;
}

function SidebarContent({ collapsed, onNavigate, onToggleCollapse, tourAnchors }: SidebarContentProps) {
  const { brand, shortName, kicker } = useBrand();
  const { src: logoSrc, onError: onLogoError } = useBrandLogo(brand, 'page');
  // Current route — stashed on the Feedback link so the Feedback page can record
  // where the user was when they reached for it (the old modal's `context`).
  const { pathname } = useLocation();
  // Waiting Steward-team replies. Mounting the shared ['feedback'] query here is
  // what makes the badge appear without visiting the page; its 5-minute
  // staleTime is what keeps that from re-scanning the partition on every focus.
  const { count: unreadReplies } = useFeedbackUnread();
  const feedbackLabel = feedbackNavLabel(unreadReplies);
  // The Announcements badge (steward-app#2612): same shapes as Feedback's, same reasoning.
  const { count: unreadAnnouncements } = useAnnouncementsUnread();
  const announcementsLabel = announcementsNavLabel(unreadAnnouncements);
  // An open onboarding request from a firm (steward-app#1427): a DOT on People, never a
  // number, in both nav modes, and it clears only when the family confirms. Reads the same
  // query as the Dashboard card and the notice on People, and is `false` for anyone who is not
  // family in this workspace (the query is not even sent for them).
  const onboardingRequestWaiting = useOnboardingRequestWaiting();
  // UX gating only (useAdvisorFirm's header) — this decides whether the advisor items
  // below are offered, never whether the pages they link to can read anything.
  const { advisorFor, isLoading: advisorFirmLoading, isError: advisorFirmErrored } = useAdvisorFirm();
  // steward-app#643 — defence in depth only (useCredentialsReadProbe's header): appcore's
  // `readCapability` gate is what actually refuses an advisor's read, not this filter.
  // `settled` is read separately, below `inClientWorkspace`, for the steward-app#1402 hold
  // — this destructure only pulls the fail-open boolean the rest of this file used before.
  const { canRead: canReadCredentials, settled: credentialsProbeSettled } = useCredentialsReadProbe();
  const canReadCashFlow = useCanReadCashFlow();
  const { activeWorkspaceId, activeMembership } = useWorkspace();
  const workspaceKind = useWorkspaceKind();
  // The one source of truth for "is this session inside a client's workspace, looking in
  // on it as an advisor" (steward-app#1401) — see that hook's own header for the full
  // account of `inFirmWorkspace` / `inClientFrame` / `inClientWorkspace`, including why
  // `inClientFrame` is NOT simply "not the firm workspace" (a firm's own staff member in
  // their own family workspace hits `inClientWorkspace` too — which is exactly why
  // steward-app#1402's credentials-probe hold below reads THAT broader field, never
  // `inClientFrame`: the probe's own cold-load flash is no less real in that same-family
  // case, and narrowing the hold to `inClientFrame` would drop it there). `clientName` and
  // `exitToClients` feed the nav frame below; `ViewOnlyPill` and `Breadcrumbs` call the
  // same hook independently rather than being threaded a value from here, so none of the
  // four consumers can disagree.
  const { advisorNavVisible, inFirmWorkspace, inClientWorkspace, inClientFrame, clientName, exitToClients } =
    useClientFrame();

  // The same cold-load flash `HomeRoute` guards against, and now literally the same code:
  // `useAdvisorFirm()`'s query hasn't settled yet on a fresh load, so `advisorFor` reads
  // `null` — indistinguishable here from "not an advisor" — and without this, an advisor's
  // every session would paint all six pillars and drop them a beat later once the query
  // resolves. `useHoldForAdvisorQuery` (steward-app#794) owns both the match condition and
  // the TIME BOUND on the hold; read its header before touching this, in particular for why
  // the nav falls back to the ordinary family pillars once that bound elapses rather than
  // going on waiting out a hanging request.
  const holdForAdvisorQuery = useHoldForAdvisorQuery(advisorFirmLoading, activeWorkspaceId);
  // A SECOND, independent cold-load flash (steward-app#1402): `useCredentialsReadProbe`
  // answers per-WORKSPACE (its own header), so entering ANY client workspace is a cold
  // query key for it, not just an advisor's first-ever session. Without this, `nav` below
  // would paint Credentials from `canReadCredentials`'s fail-open default, then drop it a
  // beat later once the probe answers `false` for the advisor's read-only role.
  //
  // Two OR-ed reasons to hold, both gated on `!credentialsProbeSettled` and bounded by the
  // same `ADVISOR_HOLD_MAX_MS`:
  //  - `inClientWorkspace`: the ordinary case, once `advisorFor` has resolved and this
  //    workspace is known to be a client's (or the advisor's own personal workspace —
  //    `useClientFrame`'s own header explains why that hits this branch too).
  //  - `advisorFirmLoading && rememberedFirmWorkspaceId() !== null`: a COLD load straight
  //    into a client workspace, code review of this PR (steward-app#1402) caught this —
  //    `advisorFor` is still `null` while `useAdvisorFirm()`'s own query is in flight, so
  //    `inClientWorkspace` reads `false` and the first branch never engages, even though
  //    this render is heading for the client-workspace nav once BOTH queries settle. A
  //    BROWSER that has never resolved an advisor gets `rememberedFirmWorkspaceId() ===
  //    null` (see that function's header — it is written only once a real
  //    `GET /advisor/firms` resolves an `advisorFor`, and it is per-BROWSER `localStorage`,
  //    not per signed-in user), so this term is `false` there on every render, cold load
  //    included, and that browser's nav is exactly as unbounded-fail-open as
  //    `canReadCredentials`'s own header describes. It is NOT per-user: a family member who
  //    signs in on a browser an advisor previously used there, or an advisor whose firm
  //    relationship has since ended, DOES find a leftover hint — sign-out does not clear it
  //    (only a settled `useAdvisorFirm()` query does, in either direction, per
  //    `rememberedFirmWorkspaceId`'s header) — so either gets ONE held cold load, bounded by
  //    the same `ADVISOR_HOLD_MAX_MS` as everything else here, after which their OWN
  //    `GET /advisor/firms` settles without an `advisorFor` and clears the stale hint for
  //    every render after.
  //
  // `activeWorkspaceId` as the `episodeKey` (steward-app#1402 code review): the credentials
  // probe is keyed per-workspace, so switching from client A to client B mid-hold is a NEW
  // episode with its own fresh window, never A's spent one — see `useBoundedHold`'s own
  // header for why a bare boolean bound gets this wrong on the very first render after a
  // switch. `holdForAdvisorQuery` above passes no key: its condition is pinned to ONE
  // specific remembered workspace already (`activeWorkspaceId === rememberedFirmWorkspaceId()`
  // inside that hook), so there is only ever one episode to begin with.
  const holdForCredentialsProbe = useBoundedHold(
    (inClientWorkspace || (advisorFirmLoading && rememberedFirmWorkspaceId() !== null)) && !credentialsProbeSettled,
    ADVISOR_HOLD_MAX_MS,
    activeWorkspaceId,
  );
  const familyNav = canReadCredentials ? NAV : NAV.filter((item) => item.to !== '/credentials');
  const nav = inFirmWorkspace || holdForAdvisorQuery || holdForCredentialsProbe
    ? []
    : canReadCashFlow
      ? familyNav.flatMap((item) => (item.to === '/entities' ? [CASH_FLOW_NAV_ITEM, item] : [item]))
      : familyNav;
  return (
    <>
      {/* Brand */}
      <div className={`pt-9 pb-6 flex justify-center ${collapsed ? 'px-4' : 'px-6'}`}>
        <Link to="/" onClick={onNavigate} className={`no-underline min-w-0 ${collapsed ? '' : 'block text-center'}`}>
          {collapsed ? (
            // Deliberately still the first-character treatment, never the logo image,
            // even when one is available: a narrow collapsed rail has no room for a
            // full wordmark, and scaling one down to fit would make it illegible. If
            // you're looking at this because a firm's collapsed rail looks "wrong",
            // the fix is a design decision (initial vs. a purpose-built mark icon
            // field), not swapping in `logoSrc` here.
            <div className="font-display font-semibold text-[24px] text-navy leading-none">{shortName.charAt(0)}</div>
          ) : (
            // Centered logo lockup (matches the chosen wordmark treatment): the
            // kicker eyebrow, product wordmark, and bronze rule stack on one
            // centered axis — intentionally centered even though the nav below
            // is left-aligned. Under a white-label brand the pieces come from
            // the brand doc (docs/17); Steward defaults otherwise. The wordmark
            // itself prefers the firm's logo image (useBrandLogo — same decision
            // BrandLockup and the intake header use), falling back to text when
            // there's no asset for this theme or it fails to load.
            <>
              {kicker && (
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate mb-1">
                  {kicker}
                </div>
              )}
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={shortName}
                  height={28}
                  className="mx-auto h-7 w-auto"
                  onError={onLogoError}
                />
              ) : (
                <div className="font-display font-semibold text-[21px] text-navy uppercase tracking-[0.16em]">
                  {shortName}
                </div>
              )}
              <div className="mt-2 h-px w-8 bg-bronze mx-auto" />
              <WithSteward className="mt-2 text-[11px] text-slate" />
            </>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav aria-label="Primary" data-tour={tourAnchors ? 'nav' : undefined} className="flex-1 px-3 pt-1">
        {/* The advisor frame (steward-app#1401, replacing the two-section nav docs/25 §2
            first shipped): exactly one of two shapes, never both.
             - Firm workspace: the advisor's own items ARE the whole nav (steward-app#700)
               — no exit item (there is nowhere to exit TO from home), no label.
             - A CLIENT'S workspace (`inClientFrame`, not merely `inClientWorkspace` —
               see `useClientFrame`'s header on why those differ): a single exit item
               ("Clients", calling `exitToClients`) replaces the advisor items entirely
               — the advisor never has two homes, so there is nothing left to link to
               from inside a client — followed directly by the workspace-name label and
               the family pillars below. No divider: this is one continuous list, not
               two sections trying to both read as "mine".
             - The firm's own staff member in THEIR OWN family workspace
               (`inClientWorkspace && !inClientFrame`): neither branch below renders —
               plain family pillars only, exactly like any other family member. They
               reach the firm through the workspace switcher, same as anyone else with
               more than one workspace; #804 (that workspace also appearing on the
               Clients page) is a separate, open issue.

             `advisorNavVisible &&` on Overview/Clients is LOAD-BEARING, not redundant
             (steward-app#1271): `inFirmWorkspace` alone goes true off the mounted PACK,
             which can be true before this caller's OWN `GET /advisor/firms` has
             resolved to anything (`advisorFor` still null, no `firmChoice` either). In
             that window there is no CONFIRMED advisor relationship to link Overview or
             Clients to yet, so those two must render nothing (matching `nav`'s own `[]`
             for the same window) rather than items with nothing behind their own gate.

             `firmAdmin` (steward-app#1742 review round 2, R2) is INDEPENDENT of
             `advisorNavVisible` on purpose: it stays true through that same loading/error
             window, so "Firm" is now offered while Overview and Clients are not — see
             `advisorNavItems`'s own header for why that split is correct rather than a
             third inconsistent state.

             ⚠️ **Correction (steward-app#1742 review round 2, R5): an earlier version of this
             comment claimed `isFirmAdminWorkspace`'s degraded-packs-and-unsettled-lookup admit
             branch "cannot fire from this call site" because `advisorFor` would still be
             `null` mid-flight. That is not guaranteed.** `packsResolved === false` (the
             admit branch's own gate) and `isLoading || isError` (`firmAdmin`'s own
             `firmLookupUnsettled` argument) describe two INDEPENDENT queries — `/me`'s
             workspace read and this caller's workspace-less `GET /advisor/firms` — and React
             Query keeps a query's last-fetched `data` cached across a SUBSEQUENT errored or
             in-flight refetch by default, so `advisorFor` can still hold a previously-resolved,
             MATCHING value while this same lookup is simultaneously unsettled. What that does
             NOT do is change what `firmAdmin` answers here: for the `packsResolved === false`
             quadrant, `isFirmWorkspace`'s only path to `true` is its own stand-in arm — the
             very same `advisorFor` match the admit branch would otherwise short-circuit past —
             so whichever of `isFirmAdminWorkspace`'s two branches actually answers, it answers
             the SAME way at this call site. The admit branch is redundant here, not
             unreachable; the two are NOT equivalent at `RequireFirmAdmin.tsx`'s own call,
             where an `isLoading` check runs before that stand-in arm would ever be reached,
             and the admit branch is what keeps that page from a hard refusal during this
             exact window. */}
        {(() => {
          const firmAdmin = isFirmAdminWorkspace(
            activeMembership,
            workspaceKind,
            activeWorkspaceId,
            advisorFor,
            advisorFirmLoading || advisorFirmErrored,
          );
          return inFirmWorkspace && (advisorNavVisible || firmAdmin) && (
            advisorNavItems(advisorNavVisible, firmAdmin).map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={onNavigate}
                aria-label={collapsed ? label : undefined}
                className={({ isActive }) => navLinkClass(isActive, collapsed)}
              >
                <Icon size={17} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0" />
                {!collapsed && label}
                {collapsed && <span role="presentation" className={railTooltipClass}>{label}</span>}
              </NavLink>
            ))
          );
        })()}
        {inClientFrame && (
          <button
            type="button"
            onClick={() => { exitToClients(); onNavigate?.(); }}
            aria-label={collapsed ? 'Clients' : undefined}
            className={navLinkClass(false, collapsed)}
          >
            <ArrowLeft size={17} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0" />
            {!collapsed && 'Clients'}
            {collapsed && <span role="presentation" className={railTooltipClass}>Clients</span>}
          </button>
        )}

        {/* Names the workspace whose pillars follow — deliberately the workspace name
            ONLY, never "Client: <name>" (see `useClientFrame`'s header for why). Hidden
            on the collapsed rail, where no label renders at all. */}
        {!collapsed && inClientFrame && clientName && (
          <div className="min-w-0 truncate px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate">
            <UserText>{clientName}</UserText>
          </div>
        )}

        {nav.map(({ to, label, icon: Icon, end }) => {
          // Only People carries a dot. Its name has to say why when the dot shows, because the
          // dot itself is `aria-hidden` (the `feedbackNavLabel` reasoning).
          const dot = to === '/people' && onboardingRequestWaiting;
          const name = dot ? peopleNavLabel(true) : label;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onNavigate}
              aria-label={collapsed || dot ? name : undefined}
              className={({ isActive }) => navLinkClass(isActive, collapsed)}
            >
              <Icon size={17} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0" />
              {!collapsed && label}
              {dot && <span aria-hidden data-testid="people-request-dot" className={navBadgeDotClass} />}
              {collapsed && <span role="presentation" className={railTooltipClass}>{name}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Announcements: a standing history of improvements, directly above Feedback. It is
          permanent (never hidden when read), and carries the Feedback badge shapes. */}
      <div className="px-3 pb-1">
        <NavLink
          to="/announcements"
          onClick={onNavigate}
          aria-label={collapsed || unreadAnnouncements > 0 ? announcementsLabel : undefined}
          className={({ isActive }) => navLinkClass(isActive, collapsed)}
        >
          <Megaphone size={17} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0" />
          {!collapsed && 'Announcements'}
          {!collapsed && unreadAnnouncements > 0 && (
            <span aria-hidden className={navBadgeClass}>{unreadAnnouncements}</span>
          )}
          {collapsed && unreadAnnouncements > 0 && <span aria-hidden className={navBadgeDotClass} />}
          {collapsed && <span role="presentation" className={railTooltipClass}>{announcementsLabel}</span>}
        </NavLink>
      </div>

      {/* Feedback — pinned bottom-left, above Settings. Its own destination (a full
          page, not a dialog) so it's always one click away and reads as a standing
          invitation rather than a fleeting modal. On mobile it rides in this same
          drawer, alongside Settings, rather than hiding in the account menu. */}
      <div className="px-3 pb-1" data-tour={tourAnchors ? 'nav-feedback' : undefined}>
        <NavLink
          to="/feedback"
          state={{ from: pathname }}
          onClick={onNavigate}
          // Labelled whenever a count exists, in BOTH modes: expanded, the text
          // node alone would read as "Feedback 2", and collapsed the dot says
          // nothing at all.
          aria-label={collapsed || unreadReplies > 0 ? feedbackLabel : undefined}
          className={({ isActive }) => navLinkClass(isActive, collapsed)}
        >
          <MessageSquare size={17} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0" />
          {!collapsed && 'Feedback'}
          {!collapsed && unreadReplies > 0 && (
            <span aria-hidden className={navBadgeClass}>{unreadReplies}</span>
          )}
          {collapsed && unreadReplies > 0 && <span aria-hidden className={navBadgeDotClass} />}
          {collapsed && <span role="presentation" className={railTooltipClass}>{feedbackLabel}</span>}
        </NavLink>
      </div>

      {/* Settings — pinned bottom-left, below the pillars: it configures the whole
          workspace (members, connections), not just the signed-in user, so it lives
          in the app's territory (sidebar) rather than the identity menu. */}
      <div className="px-3 pb-1" data-tour={tourAnchors ? 'nav-settings' : undefined}>
        <NavLink
          to="/settings"
          onClick={onNavigate}
          aria-label={collapsed ? 'Settings' : undefined}
          className={({ isActive }) => navLinkClass(isActive, collapsed)}
        >
          <SettingsIcon size={17} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0" />
          {!collapsed && 'Settings'}
          {collapsed && <span role="presentation" className={railTooltipClass}>Settings</span>}
        </NavLink>
      </div>

      {/* Collapse toggle (desktop only) */}
      {onToggleCollapse && (
        <div className={`px-3 pb-2 ${collapsed ? 'flex justify-center' : ''}`}>
          <button
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`group relative flex items-center gap-2.5 px-3 py-2.5 rounded-token text-[13px] font-normal text-slate bg-transparent border-none cursor-pointer transition-colors duration-150 hover:text-navy ${
              collapsed ? 'justify-center w-auto' : 'w-full'
            }`}
          >
            {collapsed
              ? <ChevronRight size={17} strokeWidth={ICON_STROKE} aria-hidden />
              : <ChevronLeft size={17} strokeWidth={ICON_STROKE} aria-hidden />}
            {!collapsed && 'Collapse'}
            {collapsed && <span role="presentation" className={railTooltipClass}>Expand sidebar</span>}
          </button>
        </div>
      )}

    </>
  );
}

/**
 * The assistant's entry point in the chrome (docs/24 P5).
 *
 * One control, two behaviours by width: at `xl:` and up it toggles the docked panel; below
 * that — where a 380px panel and a readable content column do not both fit — it stays the
 * link to `/ask` it has always been. On `/ask` itself it is always the link: the page *is*
 * the assistant, and docking a second copy of the open conversation beside it is nonsense.
 *
 * The width test is JavaScript rather than a pair of `xl:`-gated elements, because this is a
 * behaviour switch, not a style one: rendering both and hiding one leaves a control in the
 * DOM that is out of the accessibility tree but still findable by other means, and gives the
 * page two elements claiming the same job.
 *
 * It carries the tour's `ask` anchor (steward-app#583), on both branches — this is now the
 * only assistant entry point, the Dashboard launcher that used to hold the anchor having been
 * removed. A missing anchor is skipped in silence (tour/steps.ts), so an anchor on one branch
 * only would drop the step for whichever width rendered the other.
 *
 * **Suppressed in the advisor firm's own workspace** (steward-app#1541). Matt: "it's ok and
 * even valuable to have it for firms, but I don't think the prompt and setup works for firms
 * yet" — a deferral, not a decision that firms never get Ask, so this hides the entry point
 * rather than touching the assistant itself. `inFirmWorkspace` comes from `useClientFrame`,
 * the same discriminator `SidebarContent` in this file already reads for the nav frame; the
 * `/ask` route itself carries the matching defensive guard in `router.tsx`
 * (`RequireFamilyWorkspace`) for a bookmarked or hand-typed URL.
 *
 * **steward-app#1815 lifts that deferral behind `firmAssistantEnabled()`**: with
 * the flag on, the firm workspace gets the control, and the façade answers from the firm's
 * own records with a firm prompt. With it off, everything above still holds.
 */
function AskControl() {
  const { pathname } = useLocation();
  const { panelOpen, canDockPanel, openPanel, closePanel } = useAssistant();
  // steward-app#932 phase 2 — a principal without `assistant:read` gets no entry point,
  // and `/ask` tells them why (`AssistantUnavailable`). UX and honesty only: appcore's
  // `readCapability` on `chat-session` is what refuses the transcripts, not this line.
  // Fails open while the probe is in flight (`useCanUseAssistant`), so a family member
  // never sees their own Ask control blink out of the top bar on a slow network.
  const canUseAssistant = useCanUseAssistant();
  // steward-app#1541 — the same `inFirmWorkspace` `SidebarContent` above already reads;
  // see this function's own header for why.
  const { inFirmWorkspace } = useClientFrame();

  if (!canUseAssistant || (inFirmWorkspace && !firmAssistantEnabled())) return null;

  const base = 'flex h-9 max-sm:h-11 max-sm:min-w-11 max-sm:justify-center shrink-0 items-center gap-1.5 rounded-token px-2.5 no-underline text-[13px] transition-colors duration-150 border-none cursor-pointer font-sans';
  const active = 'bg-cream text-navy font-semibold';
  const idle = 'bg-transparent text-slate hover:bg-cream hover:text-navy';

  if (canDockPanel && pathname !== '/ask') {
    return (
      <button
        type="button"
        id={ASSISTANT_TRIGGER_ID}
        onClick={panelOpen ? closePanel : openPanel}
        aria-label={`Ask ${STEWARD}`}
        aria-expanded={panelOpen}
        aria-controls={ASSISTANT_PANEL_ID}
        data-tour="ask"
        className={`${base} ${panelOpen ? active : idle}`}
      >
        <Sparkles size={17} strokeWidth={ICON_STROKE} aria-hidden />
        <span className="hidden sm:inline">Ask {STEWARD}</span>
      </button>
    );
  }

  return (
    <NavLink
      to="/ask"
      id={ASSISTANT_TRIGGER_ID}
      aria-label={`Ask ${STEWARD}`}
      data-tour="ask"
      className={({ isActive }) => `${base} ${isActive ? active : idle}`}
    >
      <Sparkles size={17} strokeWidth={ICON_STROKE} aria-hidden />
      <span className="hidden sm:inline">Ask {STEWARD}</span>
    </NavLink>
  );
}

/**
 * The client frame's own banner (steward-app#1401), replacing the bare "View only" pill
 * that used to be the only signal an advisor was standing in a client's workspace. Renders
 * in the same page-column slot as {@link OfflineBanner} (this file's `AppShell`), so it
 * reads from any page rather than only the top bar.
 *
 * Bronze rather than the neutral tones the rest of the chrome uses: this is a MODE, not a
 * status message, and it needs to stay legible glancing down from any point on the page,
 * not just the instant it first appears. `role="status"` (not `alert`): nothing here is
 * urgent, and an advisor who has been in this client for a while should not be interrupted
 * by it on every re-render.
 *
 * Suppressed while `clientName` hasn't loaded yet (`useClientFrame`'s own doc on why),
 * matching the sidebar label's "omitted, not shown empty" rule — the alternative is a
 * sentence with a hole in it for one frame.
 *
 * ⚠️ **No noun around the name, and that is load-bearing in both directions**
 * (steward-app#1436, founder decision 2026-09-15). This read "Viewing the {name} family as
 * their advisor" until then, which was wrong twice over. It said "family" on the one piece
 * of chrome only an advisor ever reads, against #1431's ruling that the firm side says
 * client. And `clientName` is `activeMembership.workspaceName`, a name the household typed
 * into a field whose own placeholder is "e.g. The Anderson Family"
 * (`OnboardingFlow.tsx`), so the wrapper produced "Viewing the The Anderson Family family
 * as their advisor." for every workspace named that way. Do not reintroduce a noun here:
 * the name is the only thing that can safely sit in this slot, because this component does
 * not know what kind of thing the household called itself. `Layout.test.tsx` asserts the
 * whole sentence, not a fragment, for exactly this reason.
 */
function ClientFrameBand() {
  const { inClientFrame, clientName, clientReadOnly, exitToClients } = useClientFrame();
  if (!inClientFrame || !clientName) return null;
  return (
    <div
      role="status"
      className="flex flex-col gap-2 border-b border-bronze/35 bg-bronze/10 px-4 py-2 font-sans text-[12px] text-navy sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6"
    >
      <span className="flex items-center gap-1.5">
        <Eye size={14} strokeWidth={ICON_STROKE} aria-hidden className="shrink-0 text-slate" />
        {/* `clientReadOnly` (`useClientFrame`'s own doc): a contributing advisor genuinely
            holds `inst:write` on this firm's shared folder, so "Read-only." is dropped for
            that membership rather than asserted falsely — see that doc for the full
            argument this branch's own security review produced. */}
        <span>
          Viewing <UserText>{clientName}</UserText> as their advisor.{clientReadOnly && ' Read-only.'}
        </span>
      </span>
      <button
        type="button"
        onClick={exitToClients}
        className="inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 self-start rounded-token border border-bronze/50 bg-transparent px-3 font-sans text-[12px] font-semibold text-navy cursor-pointer transition-colors duration-150 hover:bg-bronze/15 sm:min-h-0 sm:self-auto sm:py-1"
      >
        <ArrowLeft size={14} strokeWidth={ICON_STROKE} aria-hidden />
        Back to clients
      </button>
    </div>
  );
}

/**
 * The bare shell's own header (steward-app#1828): a memberless account (`/me` has
 * loaded and there is no active membership — see `AppShell`'s own condition) gets
 * none of the ordinary chrome — no sidebar, no workspace switcher, no breadcrumbs,
 * no Ask — so this is the whole header: the wordmark, left, so the page still
 * reads as the product, and the account menu, right, so Sign out and (via the
 * menu's own `bare` variant) Account settings stay reachable. Deliberately NOT
 * `BrandLockup` (centered, stacked, built for a full-page arrival moment like
 * `FounderArrival`) — this is ordinary standing chrome, and a left-aligned mark
 * matching this file's own sidebar wordmark treatment reads as such.
 */
function BareTopBar() {
  const { brand, shortName } = useBrand();
  const { src: logoSrc, onError: onLogoError } = useBrandLogo(brand, 'page');
  return (
    <header className="min-h-16 pt-[env(safe-area-inset-top)] bg-surface/90 backdrop-blur-[8px] border-b border-line/60 sticky top-0 z-40 flex items-center gap-2 px-3 sm:px-5 lg:px-10">
      {logoSrc ? (
        <img src={logoSrc} alt={shortName} height={24} className="h-6 w-auto" onError={onLogoError} />
      ) : (
        <div className="font-display font-semibold text-[16px] text-navy uppercase tracking-[0.14em]">
          {shortName}
        </div>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-1 pl-1 sm:pl-3">
        <AccountMenu bare />
      </div>
    </header>
  );
}

/**
 * The bare shell's body (steward-app#1828): `BareTopBar` above, then the page
 * content — still through the ordinary `Outlet`, so every page this can wrap
 * (today, the paths in `MEMBERLESS_ALLOWED_PATHS` except the four `/onboarding*`
 * ones: the picker and the identity-scoped Settings tabs; the onboarding paths sit in that list
 * but are routed outside `Layout` entirely, see the sibling route in `router.tsx`) renders unmodified inside it. `OfflineBanner`
 * stays, matching the full shell (`AppShell`'s own comment on why it sits in
 * the page column rather than above the row); there is no docked assistant
 * panel here to make room for, so unlike the full shell this has no second
 * column to reason about.
 */
function BareShellBody() {
  return (
    <div className="flex min-h-screen flex-col">
      <BareTopBar />
      <OfflineBanner />
      <main className="flex-1 px-6 py-8 lg:px-12 lg:py-10">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * The global header. **Its 64px (`min-h-16`) height is load-bearing beyond this component:**
 * the docked assistant panel now sits underneath it and offsets itself by exactly that much
 * (`sticky top-16 h-[calc(100vh-4rem)]` in `AssistantPanel.tsx`, steward-app#585). Change the
 * height here and that offset must change with it, or the panel hangs below the fold.
 *
 * A measured offset was considered and declined: this is `flex items-center` with no wrap, so
 * the row can only exceed 64px if a *child* does, and the tallest at `xl:` (where the panel
 * exists at all) is the h-9 Ask control. `env(safe-area-inset-top)` is 0 on desktop. The
 * failure mode if that ever stops holding is graceful anyway — this header's explicit `z-40`
 * paints over the unlayered sticky panel, clipping its top strip rather than interleaving.
 */
function TopBar({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  return (
    <header className="min-h-16 pt-[env(safe-area-inset-top)] bg-surface/90 backdrop-blur-[8px] border-b border-line/60 sticky top-0 z-40 flex items-center gap-2 px-3 sm:px-5 lg:px-10">
      <button
        onClick={onOpenDrawer}
        aria-label="Open navigation"
        className="lg:hidden -ml-2 flex h-9 w-9 max-sm:h-11 max-sm:w-11 shrink-0 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-navy"
      >
        <Menu size={19} strokeWidth={ICON_STROKE} aria-hidden />
      </button>
      <WorkspaceSwitcher />
      <EnvPill />
      <ViewOnlyPill />
      <Breadcrumbs />
      <div className="ml-auto flex shrink-0 items-center gap-1 pl-1 sm:pl-3">
        <AskControl />
        <AccountMenu />
      </div>
    </header>
  );
}

export function Layout() {
  // A firm's own workspace keeps its assistant conversation in memory only (steward-app#1815).
  const { inFirmWorkspace, kindUnresolved } = useClientFrame();
  return (
    <BreadcrumbProvider>
    <TourProvider>
    {/* The one conversation, shared by every surface that renders it (docs/24). It wraps the
        whole authenticated shell so the `/ask` page and the side panel resolve the same
        instance and cannot disagree about consent, thread, or quota. Inside `TourProvider`
        because it closes the panel when the tour starts; nothing about the tour reads it. */}
    <AssistantProvider firmConversation={inFirmWorkspace} kindUnresolved={kindUnresolved}>
      <AppShell />
    </AssistantProvider>
    </TourProvider>
    </BreadcrumbProvider>
  );
}

/** The shell proper, one component inside the providers so it can read the assistant's
 *  panel state — the docked panel is a sibling of the whole content column, and the left nav
 *  makes room for it. */
function AppShell() {
  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  // steward-app#1828 — the bare-shell condition, decided founder-approved 2026-09-25
  // (D1): "`/me` has loaded AND there is no active membership", never a route match.
  // `meLoaded` is what makes this NOT fire during the ordinary loading window — a
  // member's first paint still gets whatever this function renders below while /me is
  // in flight (the same shell it always has), never a flash of the bare page first.
  // `activeMembership` is WorkspaceProvider's own `memberships.find(...) ?? null`, so
  // it reads `null` for exactly two reasons: /me hasn't resolved yet, or it resolved
  // and this account genuinely has no usable workspace — `meLoaded` is what tells
  // those apart, since `activeMembership` alone cannot. Deliberately NOT gated on
  // `pathname`: the design is "regardless of path" (issue #1828), and in practice the
  // only paths a memberless account can even reach are `MEMBERLESS_ALLOWED_PATHS`
  // (`WorkspaceProvider.tsx`) — this reads no narrower list of its own, so the two
  // cannot drift apart.
  const { meLoaded, activeMembership } = useWorkspace();
  const bare = meLoaded && !activeMembership;
  // crossmarc/appcore#739: copies a firm-onboarded client's tax id from the onboarding form onto
  // their household's person rows, silently. Renders nothing; see the hook. Here in the shell,
  // mounted once, and not in `SidebarContent`, which renders twice (sidebar and drawer).
  useOnboardingTaxIdCopy();
  useOnboardingSelfLink();
  // Already means "open AND wide enough to dock" — the provider owns the breakpoint (P4: at
  // 1280 with the nav expanded the content column is 1280−272−380 = 628px, and `main`'s
  // `lg:px-12` takes it to ~532px, which is phone width still rendering the `lg:` layout).
  const { panelOpen: panelRequested } = useAssistant();
  // The panel's own gate (#932 phase 2), now carrying steward-app#1541's workspace-kind
  // check too. Not redundant with `AskControl` returning null: the panel's open state is
  // RESTORED from storage at mount (`assistant/panelStorage.ts`) regardless of which
  // workspace the stored entry names, so a session that switches into a client workspace
  // with the panel already docked — or an advisor who opened it in a client workspace and
  // then switches into their OWN firm workspace, `Layout` never unmounting either way —
  // would otherwise keep a chat surface on screen that no control could have opened.
  //
  // Folded into `panelDocked` rather than applied at the mount site, so every consequence
  // of a docked panel goes with it: without this the nav would still rail to 76px and the
  // collapse toggle would still be suppressed, leaving 380px of empty gutter beside a
  // squeezed page for a principal who has no panel.
  const canUseAssistant = useCanUseAssistant();
  const { inFirmWorkspace } = useClientFrame();
  // steward-app#1815: the firm workspace docks it too once `firmAssistantEnabled()`, the same
  // switch `AskControl` reads, so the two cannot disagree.
  const panelDocked = panelRequested && canUseAssistant && (!inFirmWorkspace || firmAssistantEnabled());
  const tour = useTourOptional();

  // ...which is why opening the panel also rails the nav: 76px instead of 272px leaves ~728px
  // for the page. Temporary and NOT persisted — `collapsed` (the user's own preference, and
  // the only thing written to storage) is untouched, so closing the panel restores whatever
  // they had.
  const navRailed = collapsed || panelDocked;

  // The width animation is skipped for the one transition nobody is watching: the tour's.
  //
  // Starting the tour closes the panel, which un-rails this sidebar — 196px of movement, over
  // 200ms. `TourOverlay` measures its first anchor once, in a layout effect at mount, and
  // re-measures only on scroll and resize, neither of which a CSS width transition fires. So
  // the opening spotlight would be placed against a layout still in motion and left there.
  // Snapping instead of animating is invisible (the tour's own scrim is arriving over it) and
  // makes the measurement true.
  const navTransition = tour?.starting ? '' : 'transition-[width] duration-200';

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore storage failures (e.g. private browsing)
    }
  }, [collapsed]);

  // Close the drawer on any route change (covers browser back/forward too).
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // steward-app#1828 — the bare shell, in place of everything below. `EnvTopStrip`,
  // `DocumentTitle` and `UnsavedChangesGuard` stay mounted either way (the founder's
  // own list for what stays, D1): none of the three is chrome a memberless account
  // needs hidden, and a Settings form (the one page family this shell wraps) can
  // still go dirty and needs the same leave-guard as anywhere else.
  if (bare) {
    return (
      <>
      <EnvTopStrip />
      <DocumentTitle />
      <UnsavedChangesGuard />
      <BareShellBody />
      </>
    );
  }

  return (
    <>
    <EnvTopStrip />
    <DocumentTitle />
    <UnsavedChangesGuard />
    <div className="flex min-h-screen">
      {/* Desktop sidebar (≥1024px) */}
      <aside
        className={`hidden lg:flex shrink-0 bg-surface border-r border-line shadow-rail-r flex-col sticky top-0 z-30 h-screen ${navTransition} ${
          navRailed ? 'w-[76px]' : 'w-[272px]'
        }`}
      >
        {/* No collapse toggle while the panel holds the rail open: there is nothing it could
            do, and a button that visibly does nothing is worse than an absent one. */}
        <SidebarContent
          collapsed={navRailed}
          onToggleCollapse={panelDocked ? undefined : () => setCollapsed(c => !c)}
          tourAnchors
        />
      </aside>

      {/* Mobile drawer (<1024px) — always expanded; the rail is desktop-only (docs/04 §8) */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setDrawerOpen(false)} aria-hidden />
          <aside className="absolute inset-y-0 left-0 flex w-[272px] flex-col bg-surface border-r border-line shadow-[10px_0_30px_-15px_rgba(15,23,42,0.2)]">
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close navigation"
              className="absolute right-2 top-[calc(0.5rem+env(safe-area-inset-top))] flex h-9 w-9 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-slate transition-colors duration-150 hover:text-navy"
            >
              <X size={17} strokeWidth={ICON_STROKE} aria-hidden />
            </button>
            <SidebarContent collapsed={false} onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onOpenDrawer={() => setDrawerOpen(true)} />

        {/* Page and panel share a row BENEATH the header (steward-app#585). The panel used to
            be a sibling of this whole column, which made the top bar stop at its left edge:
            nothing was covered (the account menu slid left with the shrinking bar), but the
            top-right corner of the window became the panel's Close button, and the app read as
            two surfaces side by side rather than one with a panel in it. The assistant is part
            of the product, so the product's header spans it.

            This costs the panel ~40px, honestly: its own header used to sit in the same
            viewport band as the top bar, so it was free. See AssistantPanel's `cls.header`.

            What did NOT change: the nav still rails when the panel docks (`navRailed` above),
            so the header's LEFT edge still moves on open. Only the right-side cluster is now
            fixed — which is the half that matters, since the Ask control there is what opens
            the panel and now sits directly above it. */}
        <div className="flex-1 flex min-w-0">
          {/* The page column. `OfflineBanner` sits INSIDE it, not above the row, and that
              placement is load-bearing rather than cosmetic: the panel's height is
              `100vh - 4rem`, which fits exactly only if its natural top is the header's
              bottom. Anything in flow above the row pushes the panel down by its height and
              hangs that much of it below the fold — with the banner showing, that is the
              composer, the panel's only input, off screen until the user scrolls. Keeping the
              banner in this column leaves the panel's top where the arithmetic assumes.

              The trade: while the panel is docked the banner spans the page rather than the
              full window. It is still directly under the sticky top bar and above all page
              content, which is what it is for. */}
          <div className="flex-1 flex flex-col min-w-0">
            <OfflineBanner />
            <ClientFrameBand />
            <main className="flex-1 px-6 py-8 lg:px-12 lg:py-10">
              <Outlet />
            </main>
          </div>

          {/* Mounted only while open — the provider keeps the conversation alive, so nothing
              is lost. */}
          {panelDocked && <AssistantPanel />}
          <AnnouncementPopup assistantOpen={panelRequested} />
        </div>
      </div>
    </div>
    </>
  );
}
